import { useEffect, useState, type FormEvent } from 'react';
import { Clock3, Gavel, Plus, ShieldAlert, XCircle } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { Auction } from '../types';
import { currencyInputToCents, formatMoney, formatTime, relativeTime } from '../utils';
import { ActionSheet, Badge, Button, EmptyBlock, Fab, Field, LoadingBlock, PageHeader, Panel, Textarea, TextInput } from '../components/ui';

const auctionTone = { active: 'active', sold: 'good', unsold: 'muted', cancelled: 'danger' } as const;
const auctionLabel = { active: '进行中', sold: '已成交', unsold: '流拍', cancelled: '已取消' } as const;

export function AuctionsPage({ onNeedAuth }: { onNeedAuth: () => void }) {
  const { user, settings, permissions, refresh } = useAuth();
  const { toast, confirm } = useUi();
  const [items, setItems] = useState<Auction[] | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bidInputs, setBidInputs] = useState<Record<string, string>>({});
  const [, setTick] = useState(0);

  const load = async () => {
    const data = await api<{ auctions: Auction[] }>('/api/auctions');
    setItems(data.auctions);
  };

  useEffect(() => {
    void load().catch(() => toast('拍卖数据加载失败', 'error'));
    const timer = window.setInterval(() => { setTick(value => value + 1); void load(); }, 30_000);
    return () => window.clearInterval(timer);
  }, [user?.id]);

  useEffect(() => {
    const timer = window.setInterval(() => setTick(value => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const openForm = () => {
    if (!user) return onNeedAuth();
    setFormOpen(true);
  };

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user) return onNeedAuth();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const end = String(form.get('endAt') || '');
    const payload = { name: String(form.get('name') || ''), description: String(form.get('description') || ''), startPriceCents: currencyInputToCents(String(form.get('startPrice') || '0')), stepCents: currencyInputToCents(String(form.get('step') || '0')), endAt: new Date(end).getTime() };
    setBusy(true);
    try {
      await api('/api/auctions', { method: 'POST', body: JSON.stringify(payload) });
      formEl.reset();
      setFormOpen(false);
      await load();
      toast('拍卖已发起', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '发起失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const bid = async (item: Auction, event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user) return onNeedAuth();
    const amountCents = currencyInputToCents(bidInputs[item.id] || '0');
    try {
      await api('/api/auctions/' + item.id + '/bids', { method: 'POST', body: JSON.stringify({ amountCents }) });
      await Promise.all([load(), refresh()]);
      setBidInputs(current => ({ ...current, [item.id]: '' }));
      toast('出价成功', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '出价失败', 'error');
    }
  };

  const quickAdd = (item: Auction, minimum: number, amount: number) => {
    const current = Number(bidInputs[item.id] || minimum / 100) || minimum / 100;
    setBidInputs(inputs => ({ ...inputs, [item.id]: String(current + amount) }));
  };

  const cancel = async (item: Auction, staff = false) => {
    const approved = await confirm({ title: staff ? '管理员撤销拍卖' : '确认要撤拍？', body: staff ? '当前领先者的冻结资金会原路退回，操作将写入审计日志。' : '拍卖将立即结束，当前领先者资金会退回。', danger: true, confirmText: '确认撤销' });
    if (!approved) return;
    try {
      await api('/api/auctions/' + item.id + '/cancel', { method: 'POST' });
      await Promise.all([load(), refresh()]);
      toast('拍卖已撤销', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '撤销失败', 'error');
    }
  };

  return (
    <>
      <PageHeader title="法拍场" description="出价最高者成交；领先出价资金会被冻结，被超过后自动退回。" />
      <ActionSheet open={formOpen} onClose={() => setFormOpen(false)} title="发起拍卖" eyebrow="NEW AUCTION" description="设置起拍价、加价幅度和结束时间。">
        <form className="stack-form" onSubmit={create}>
          <Field label="拍品名称"><TextInput name="name" maxLength={60} required placeholder="例：下界合金剑" /></Field>
          <Field label="说明"><Textarea name="description" rows={4} maxLength={600} placeholder="拍品属性、数量、交付方式" /></Field>
          <div className="form-grid"><Field label={'起拍价（' + settings.currency + '）'}><TextInput name="startPrice" type="number" min="0.01" step="0.01" required /></Field><Field label={'最低加价（' + settings.currency + '）'}><TextInput name="step" type="number" min="0.01" step="0.01" defaultValue="1" required /></Field></div>
          <Field label="结束时间"><TextInput name="endAt" type="datetime-local" required /></Field>
          <Button variant="primary" type="submit" loading={busy} className="wide"><Plus size={17} /> 发起拍卖</Button>
        </form>
      </ActionSheet>

      <Panel title={'拍卖列表' + (items ? ' · ' + items.length : '')} eyebrow="AUCTION BOARD">
        {!items ? <LoadingBlock /> : items.length === 0 ? <EmptyBlock title="暂无拍卖" description="发起第一场拍卖，让成员开始竞价。" action={<Button variant="primary" onClick={openForm}><Gavel size={16} /> 发起第一场拍卖</Button>} /> : (
          <div className="card-list">
            {items.map(item => {
              const active = item.status === 'active';
              const minimum = item.topBidderId ? item.topPriceCents + item.stepCents : item.startPriceCents;
              const mine = user?.id === item.sellerId;
              return (
                <article className="record-card auction-card" key={item.id}>
                  {active && mine && <button className="corner-action danger-text" onClick={() => void cancel(item)}><XCircle size={14} /> 撤拍</button>}
                  <header><div><span className="record-type">AUCTION</span><h3>{item.name}</h3><small>{item.sellerName} · {formatTime(item.createdAt)}</small></div><Badge tone={auctionTone[item.status]}>{auctionLabel[item.status]}</Badge></header>
                  {item.description && <p className="record-description">{item.description}</p>}
                  <div className="auction-metrics"><div><span>当前价格</span><strong>{formatMoney(item.topBidderId ? item.topPriceCents : item.startPriceCents, settings.currency)}</strong></div><div><span>领先者</span><strong>{item.topBidderName || '暂无'}</strong></div><div><span>加价幅度</span><strong>{formatMoney(item.stepCents, settings.currency)}</strong></div><div><span>{active ? '剩余时间' : '结束时间'}</span><strong><Clock3 size={14} /> {active ? relativeTime(item.endAt) : formatTime(item.endAt)}</strong></div></div>
                  {active && !mine && <form className="bid-form" onSubmit={event => void bid(item, event)}><TextInput name="amount" type="number" min={minimum / 100} step="0.01" value={bidInputs[item.id] || ''} onChange={event => setBidInputs(inputs => ({ ...inputs, [item.id]: event.target.value }))} placeholder={'最低 ' + formatMoney(minimum, settings.currency)} required /><Button variant="primary" type="submit"><Gavel size={16} /> 出价</Button></form>}
                  {active && !mine && <div className="quick-bids"><span>快捷加价</span>{[10, 50, 100].map(amount => <button type="button" key={amount} onClick={() => quickAdd(item, minimum, amount)}>+{amount}</button>)}</div>}
                  {active && mine && <p className="form-note">这是你发起的拍卖，不能参与出价。</p>}
                  {item.bids.length > 0 && <details className="bid-history"><summary>出价记录（{item.bids.length}）</summary>{item.bids.slice(0, 8).map(log => <div key={log.id}><span>{log.bidderName}</span><b>{formatMoney(log.amountCents, settings.currency)}</b><small>{formatTime(log.createdAt)}</small></div>)}</details>}
                  {active && permissions.isStaff && !mine && <footer className="record-actions"><Button variant="danger" onClick={() => void cancel(item, true)}><ShieldAlert size={16} /> 管理撤销</Button></footer>}
                </article>
              );
            })}
          </div>
        )}
      </Panel>
      <Fab label="发起拍卖" onClick={openForm} />
    </>
  );
}
